/**
 * PDFViewer Component
 *
 * Displays PDF documents using an iframe.
 * Shows download link for better viewing experience.
 */

import React from 'react';
import { Box, Typography, Paper, Button, Alert } from '@mui/material';
import { Download, PictureAsPdf } from '@mui/icons-material';

interface PDFViewerProps {
  file_url: string;
  filename?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ file_url, filename }) => {
  if (!file_url) {
    return (
      <Alert severity="warning">
        PDF file URL not available
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <PictureAsPdf color="error" />
          {filename && (
            <Typography variant="subtitle1" fontWeight="bold">
              {filename}
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<Download />}
          href={file_url}
          download={filename}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download PDF
        </Button>
      </Box>

      {/* PDF Display */}
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          height: '700px',
          overflow: 'hidden',
          bgcolor: 'grey.100'
        }}
      >
        <iframe
          src={file_url}
          title={filename || 'PDF Document'}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
        />
      </Paper>

      {/* Footer Info */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Having trouble viewing? Try downloading the PDF using the button above.
      </Typography>
    </Box>
  );
};

export default PDFViewer;
